import { useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import React, { memo, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import { Duration } from '@blade/std-lib'
import { useBorderBoxSizeCallback } from '@blade/ui-kit'
import {
  PairMigrationStatus,
  type PairMetrics,
  type PairModel,
  type PriceChartInterval,
} from '@root/entities/pair'
import { PairElementContextProvider } from '@root/entities/pair/ui/common/pair-element-context'
import { dateByMinuteAtom } from '@root/shared/lib'
import { type ClassNameProps } from '@root/shared/react'

import { PairExploreItemBondingBg } from './pair-explore-item-bg'
import { PairExploreItemBodyLarge } from './pair-explore-item-body-large'
import { PairExploreItemBodyMedium } from './pair-explore-item-body-medium'
import { PairExploreItemBodySmall } from './pair-explore-item-body-small'
import { PairExploreItemContext } from './pair-explore-item-context'
import { PairExploreItemIcon } from './pair-explore-item-icon'

const SHOW_MIGRATION_BG_DURATION = Duration.hours(1)

export type PairExploreItemSize = 'medium' | 'large' | 'small'
type Props = {
  pair: PairModel
  onClick?: (options: { pair: PairModel }) => void
  statsPeriod: PairMetrics.MetricsSpan
  priceChartInterval: PriceChartInterval
  layoutSize?: PairExploreItemSize
  withHide?: boolean
  iconSrcOverride?: string
} & ClassNameProps

export const PairExploreItem: React.FC<Props> = memo(
  ({
    pair,
    className,
    onClick,
    statsPeriod,
    priceChartInterval,
    layoutSize,
    withHide,
    iconSrcOverride,
  }) => {
    const ref = useRef<HTMLDivElement>(null)
    const [migrationStatus] = useAtom(pair.migrationStatus)
    const [bondingPercentage] = useAtom(pair.bondingPercentage)
    const [size, setSize] = useState<PairExploreItemSize | undefined>(
      layoutSize,
    )

    useBorderBoxSizeCallback(ref, (size) => {
      if (layoutSize) {
        return
      }
      if (size.inlineSize === 0) {
        return
      }
      const type = match(size.inlineSize)
        .returnType<PairExploreItemSize>()
        .when(
          (size) => size > 600,
          () => 'large',
        )
        .when(
          (size) => size > 400,
          () => 'medium',
        )
        .otherwise(() => 'small')

      setSize(type)
    })

    const [dateByMinute] = useAtom(dateByMinuteAtom)

    const Body = match(size)
      .with('large', () => PairExploreItemBodyLarge)
      .with('medium', () => PairExploreItemBodyMedium)
      .with('small', () => PairExploreItemBodySmall)
      .otherwise(() => undefined)

    const styles = useMemo(() => {
      return {
        '--bonding-pct': `${bondingPercentage}%`,
      } as React.CSSProperties
    }, [bondingPercentage])

    const bg = useMemo(() => {
      if (migrationStatus instanceof PairMigrationStatus.Bonding) {
        return <PairExploreItemBondingBg />
      }
      return undefined
    }, [migrationStatus])

    const shouldShowMigratedBg = useMemo(() => {
      if (!PairMigrationStatus.isMigrated(migrationStatus)) {
        return false
      }

      const shouldShow = DateF.isBefore(
        dateByMinute,
        DateF.addMilliseconds(
          migrationStatus instanceof PairMigrationStatus.MigratedTo ||
            migrationStatus instanceof PairMigrationStatus.MigratedFrom
            ? migrationStatus.migration.migrationTimestamp
            : 0,
          Duration.toMillis(SHOW_MIGRATION_BG_DURATION),
        ),
      )
      return shouldShow
    }, [migrationStatus, dateByMinute])

    const ctxValue = useMemo(() => ({ withHide }), [withHide])

    return (
      <PairExploreItemRoot
        className={className}
        style={styles}
        data-has-bonding={bondingPercentage !== undefined}
        data-migrated={shouldShowMigratedBg}
        ref={ref}
      >
        <PairElementContextProvider pair={pair} useLinkForBadge={true}>
          <PairExploreItemContext.Provider value={ctxValue}>
            {bg}
            {Body ? (
              // eslint-disable-next-line react-hooks/static-components
              <Body
                onClick={onClick}
                pair={pair}
                statsPeriod={statsPeriod}
                priceChartInterval={priceChartInterval}
                iconSrcOverride={iconSrcOverride}
              />
            ) : (
              <div />
            )}
          </PairExploreItemContext.Provider>
        </PairElementContextProvider>
      </PairExploreItemRoot>
    )
  },
)

export const PairExploreItemRoot = styled.div`
  --bonding-pct: 0;
  --card-border-radius: 12px;
  --boosters-badge-bg: var(--backgroundA-2);

  all: unset;
  min-height: 110px;
  border-radius: var(--card-border-radius);

  display: grid;
  cursor: pointer;

  position: relative;
  overflow: hidden;

  transition: all 0.1s linear;
  background: var(--background-2);

  box-shadow: inset 0 0 0 1px var(--backgroundA-3);

  &:hover {
    background: var(--background-3);

    & ${PairExploreItemIcon.HideButton} {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &[data-migrated='true'] {
    background:
      linear-gradient(
        97deg,
        rgba(var(--amber-6-rgb), 0.06) -2.94%,
        transparent 85.8%
      ),
      var(--background-2);

    box-shadow: inset 0 0 0 1px var(--amber-6);

    &:hover {
      background:
        linear-gradient(
          97deg,
          rgba(var(--amber-6-rgb), 0.06) -2.94%,
          transparent 85.8%
        ),
        var(--background-3);
    }
  }
`
